import { Paperclip } from "lucide-react";
import {
  useCallback,
  useRef,
  useState,
  type ComponentProps,
  type DragEvent,
  type ReactNode,
} from "react";

import { cn } from "@/lib/utils";

import { dragKindOf, draggedDocument, type DragKind } from "./documentDrag";
import { useAddFiles } from "./useAddFiles";
import { useThreadId } from "./useThreadId";

/** Where a drag over the chat goes, named by what it carries: the chat's drop area and the lip's
 * say the same. */
export function useDropTitle(kind: DragKind): string {
  return kind === "document"
    ? `Add ${draggedDocument()?.name ?? "this document"} to the chat`
    : "Add these files to the chat";
}

/** Adds what was dropped to the message being written, as AddToChatCard does. */
export function useDropFiles() {
  const { addFiles, addDocument } = useAddFiles();
  return useCallback(
    (kind: DragKind, data: DataTransfer) => {
      const document = draggedDocument();
      if (kind === "files") addFiles(data.files);
      else if (document) void addDocument(document);
    },
    [addFiles, addDocument]
  );
}

/** The drop area's look: the paperclip tile and where the files go, in a dashed frame. */
export function DropArea({ children, className, ...props }: ComponentProps<"div">) {
  return (
    <div
      className={cn(
        "flex items-center justify-center gap-3 rounded-assistant border-2 border-dashed px-4 text-center text-sm font-medium",
        className
      )}
      {...props}
    >
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary text-primary-foreground">
        <Paperclip className="h-4 w-4" aria-hidden />
      </span>
      <span className="min-w-0 break-words">{children}</span>
    </div>
  );
}

function DropOverlay({ kind }: { kind: DragKind }) {
  const title = useDropTitle(kind);
  return (
    <div
      data-agent-drop-area
      // A light tint over the chat (it stays readable), the title on a panel of its own.
      className="pointer-events-none absolute inset-2 z-10 flex items-center justify-center rounded-assistant border-2 border-dashed border-primary bg-primary/[0.08] px-4 backdrop-blur-[1px]"
    >
      <span className="flex min-w-0 items-center gap-3 rounded-xl bg-card/95 px-4 py-3 text-sm font-medium shadow-sm">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary text-primary-foreground">
          <Paperclip className="h-4 w-4" aria-hidden />
        </span>
        <span className="min-w-0 break-words">{title}</span>
      </span>
    </div>
  );
}

/**
 * The chat as a drop target: while files (or a document from the page) are dragged over it, a
 * drop area says where they go; dropping adds them to the message being written.
 */
export function AddToChatCard({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  const threadId = useThreadId();
  const dropFiles = useDropFiles();
  const [drag, setDrag] = useState<DragKind | null>(null);
  const depth = useRef(0);

  const enter = (event: DragEvent) => {
    const next = dragKindOf(event.dataTransfer.types);
    if (!next || threadId == null) return;
    event.preventDefault();
    depth.current += 1;
    setDrag(next);
  };
  const over = (event: DragEvent) => {
    if (!drag) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = "copy";
  };
  const leave = () => {
    depth.current = Math.max(0, depth.current - 1);
    if (depth.current === 0) setDrag(null);
  };
  const drop = (event: DragEvent) => {
    if (!drag) return;
    event.preventDefault();
    depth.current = 0;
    setDrag(null);
    dropFiles(drag, event.dataTransfer);
  };

  return (
    <div
      className={cn("relative flex min-h-0 flex-col", className)}
      onDragEnter={enter}
      onDragOver={over}
      onDragLeave={leave}
      onDrop={drop}
    >
      {children}
      {drag && <DropOverlay kind={drag} />}
    </div>
  );
}
